import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { 
  Lock, 
  Check, 
  ChevronRight, 
  Home as HomeIcon, 
  MapPin, 
  Calendar,
  Info,
  CreditCard,
  Heart,
  GitCompare,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '../App';
import { toast } from 'sonner';
import { resolveImageUrl, resolveThumbUrl } from '../utils/horseHelpers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const VirtualCorral = ({ user, logout }) => {
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // 1: Facilities, 2: Adoption Method, 3: Mustang Selection
  const [facilityCompleted, setFacilityCompleted] = useState(false);
  const [adoptionMethod, setAdoptionMethod] = useState(null);
  const [selectedMustangs, setSelectedMustangs] = useState([]);
  const [showMustangInfo, setShowMustangInfo] = useState(false);
  const [selectedMustangInfo, setSelectedMustangInfo] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0); // For photo gallery
  const [virtualCorralSettings, setVirtualCorralSettings] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHerdPaymentModal, setShowHerdPaymentModal] = useState(false);
  const [virtualMustangs, setVirtualMustangs] = useState([]);
  const [loadingMustangs, setLoadingMustangs] = useState(false);
  const [adoptionStatus, setAdoptionStatus] = useState(null);
  const [herdMembershipActive, setHerdMembershipActive] = useState(false);
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [rotationInfo, setRotationInfo] = useState(null);
  // Favorites & Compare state
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoriteMustangs, setFavoriteMustangs] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');

  useEffect(() => {
    checkSubscription();
    fetchSettings();
    checkFacilityStatus();
    fetchFavorites();
    fetchAdoptionStatus();
    fetchRotationInfo();
  }, []);

  const checkSubscription = async () => {
    try {
      console.log('[SUBSCRIPTION] Checking subscription status...');
      const response = await api.get('/virtual-corral/subscription');
      console.log('[SUBSCRIPTION] Response:', response.data);
      setHasSubscription(response.data.has_subscription);
      if (response.data.has_subscription) {
        console.log('[SUBSCRIPTION] User HAS active subscription until:', response.data.valid_until);
      } else {
        console.log('[SUBSCRIPTION] User does NOT have active subscription');
      }
    } catch (error) {
      console.error('[SUBSCRIPTION] Error checking subscription:', error);
      console.error('[SUBSCRIPTION] Error response:', error.response?.data);
      // If 401 unauthorized, don't assume no subscription - user might need to re-auth
      if (error.response?.status === 401) {
        console.log('[SUBSCRIPTION] User not authenticated - cannot check subscription');
      }
      // Keep hasSubscription as false on error
      setHasSubscription(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchRotationInfo = async () => {
    try {
      const response = await api.get('/virtual-mustangs/rotation-info');
      setRotationInfo(response.data);
    } catch (error) {
      console.error('Error fetching rotation info:', error);
    }
  };

  const fetchAdoptionStatus = async () => {
    try {
      const response = await api.get('/virtual-corral/adoption-status');
      setAdoptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching adoption status:', error);
    }
  };

  // Admin preview mode - bypass subscription check
  const isAdminPreview = user?.is_admin;

  const fetchSettings = async () => {
    try {
      const response = await api.get('/virtual-corral/settings');
      setVirtualCorralSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const checkFacilityStatus = async () => {
    try {
      const response = await api.get('/facility/get');
      setFacilityCompleted(response.data?.qualified || false);
    } catch (error) {
      console.error('Error checking facility:', error);
    }
  };

  // Fetch user's favorites
  const fetchFavorites = async () => {
    try {
      const response = await api.get('/user/favorites');
      setFavoriteIds(response.data.favorite_ids || []);
      setFavoriteMustangs(response.data.favorite_mustangs || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (mustangId, mustangName) => {
    const isFavorite = favoriteIds.includes(mustangId);
    
    try {
      if (isFavorite) {
        await api.post('/user/favorites/remove', { mustang_id: mustangId });
        setFavoriteIds(prev => prev.filter(id => id !== mustangId));
        setFavoriteMustangs(prev => prev.filter(m => m.id !== mustangId));
        toast.success(`Removed ${mustangName} from favorites`);
      } else {
        await api.post('/user/favorites/add', { mustang_id: mustangId });
        setFavoriteIds(prev => [...prev, mustangId]);
        // Add the mustang to favorites list if we have it
        const mustang = virtualMustangs.find(m => m.id === mustangId);
        if (mustang) {
          setFavoriteMustangs(prev => [...prev, mustang]);
        }
        toast.success(`Added ${mustangName} to favorites`);
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  // Toggle compare selection
  const toggleCompare = (mustang) => {
    const isInCompare = compareList.find(m => m.id === mustang.id);
    
    if (isInCompare) {
      setCompareList(prev => prev.filter(m => m.id !== mustang.id));
    } else {
      if (compareList.length >= 3) {
        toast.error('You can compare up to 3 mustangs at a time');
        return;
      }
      setCompareList(prev => [...prev, mustang]);
    }
  };

  const fetchVirtualMustangs = async (method) => {
    setLoadingMustangs(true);
    try {
      const response = await api.get('/virtual-mustangs', {
        params: { 
          adoption_method: method,
          _t: Date.now() // Cache buster
        }
      });
      
      // Always use real mustangs from API - no more mock data
      setVirtualMustangs(response.data || []);
      
      if (response.data.length === 0) {
        toast.info(`No ${method.toUpperCase()} mustangs available right now`);
      }
    } catch (error) {
      console.error('Error fetching virtual mustangs:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code,
        isAuthRelated: error.isAuthRelated
      });
      setVirtualMustangs([]);
      
      // Show specific error messages with better auth handling
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please refresh the page and log in again.', {
          duration: 5000,
          action: {
            label: 'Refresh',
            onClick: () => window.location.reload()
          }
        });
      } else if (error.response?.status === 403) {
        toast.error('Virtual Corral subscription required to view mustangs');
      } else if (error.code === 'ERR_NETWORK') {
        // Check if this might be auth related
        const token = localStorage.getItem('session_token');
        if (!token || error.isAuthRelated) {
          toast.error('Session issue detected. Please refresh the page and log in again.', {
            duration: 5000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload()
            }
          });
        } else {
          toast.error('Network error. Please check your connection and try again.');
        }
      } else {
        toast.error(`Failed to load mustangs: ${error.response?.data?.detail || error.message || 'Please try again.'}`);
      }
    } finally {
      setLoadingMustangs(false);
    }
  };

  const handleMustangClick = (mustang) => {
    setSelectedMustangInfo(mustang);
    setCurrentPhotoIndex(0); // Reset photo index when opening new mustang
    setShowMustangInfo(true);
  };

  // Add mustang directly to selection without dialog
  const addMustangToSelection = (mustang) => {
    const maxMustangs = virtualCorralSettings?.max_mustangs_per_event || 4;
    const ADOPTION_COST = 125;
    
    if (selectedMustangs.find(m => m.id === mustang.id)) {
      toast.error('You already selected this mustang!');
      return;
    }

    if (selectedMustangs.length >= maxMustangs) {
      toast.error(`You can only adopt ${maxMustangs} mustangs from this event!`);
      return;
    }

    // Check if user has enough coins
    const totalCost = (selectedMustangs.length + 1) * ADOPTION_COST;
    const userCoins = user?.ranch_coins || 0;
    
    if (userCoins < totalCost) {
      toast.error(`Not enough coins! You need ${totalCost} coins but have ${userCoins} coins.`);
      return;
    }

    setSelectedMustangs([...selectedMustangs, mustang]);
    toast.success(`${mustang.name} added! Cost: ${ADOPTION_COST} coins`);
  };

  const handleAdoptMustang = () => {
    if (!selectedMustangInfo) return;

    const maxMustangs = virtualCorralSettings?.max_mustangs_per_event || 4;
    
    // Check if mustang already selected
    if (selectedMustangs.find(m => m.id === selectedMustangInfo.id)) {
      toast.error('You already selected this mustang!');
      return;
    }

    // Check limit
    if (selectedMustangs.length >= maxMustangs) {
      toast.error(`You can only adopt ${maxMustangs} mustangs from this event!`);
      return;
    }

    setSelectedMustangs([...selectedMustangs, selectedMustangInfo]);
    toast.success(`${selectedMustangInfo.name} added to your selection!`);
    setShowMustangInfo(false);
  };

  const removeMustang = (mustangId) => {
    setSelectedMustangs(selectedMustangs.filter(m => m.id !== mustangId));
    toast.success('Mustang removed from selection');
  };

  // Check if user has active herd membership
  const checkHerdMembership = async () => {
    try {
      setCheckingMembership(true);
      
      // First check if user has Virtual Corral subscription (which includes Herd access)
      const vcResponse = await api.get('/virtual-corral/subscription');
      if (vcResponse.data.has_subscription) {
        console.log('[HERD CHECK] User has Virtual Corral subscription - Herd access granted');
        setHerdMembershipActive(true);
        return true;
      }
      
      // If no VC subscription, check for standalone Herd membership
      const response = await api.get('/subscriptions/active');
      const herdSub = response.data?.subscriptions?.find(sub => 
        sub.plan_type === 'herd_membership' && sub.is_active
      );
      
      console.log('[HERD CHECK] Standalone Herd membership:', !!herdSub);
      setHerdMembershipActive(!!herdSub);
      return !!herdSub;
    } catch (error) {
      console.error('Error checking herd membership:', error);
      setHerdMembershipActive(false);
      return false;
    } finally {
      setCheckingMembership(false);
    }
  };

  // Handle payment for herd membership
  const handleHerdMembershipPayment = async () => {
    setProcessingPayment(true);
    
    try {
      // Ensure we're using HTTPS for the origin URL
      const originUrl = window.location.protocol === 'https:' 
        ? window.location.origin 
        : `https://${window.location.host}`;
      
      console.log('Herd payment - Using origin URL:', originUrl);
      
      const response = await api.post('/subscriptions/create-checkout', {
        plan_type: 'herd_membership',
        origin_url: originUrl,
        coupon_code: couponCode || undefined
      });

      console.log('Herd payment - Stripe checkout URL:', response.data.url);
      
      // Ensure the Stripe URL is HTTPS
      if (response.data.url && response.data.url.startsWith('http://')) {
        response.data.url = response.data.url.replace('http://', 'https://');
      }

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  const confirmAdoptions = async () => {
    if (selectedMustangs.length === 0) {
      toast.error('Please select at least one mustang to adopt');
      return;
    }

    // Check if user has active herd membership
    const hasMembership = await checkHerdMembership();
    
    if (!hasMembership) {
      // Show payment modal if no active membership
      setShowHerdPaymentModal(true);
      return;
    }

    // Proceed with adoptions if membership is active
    try {
      console.log('🐴 Starting adoptions for:', selectedMustangs.map(m => m.name));
      
      // Check if we have an auth token before proceeding
      const token = localStorage.getItem('session_token');
      console.log('[ADOPT] Auth token present:', !!token);
      
      if (!token) {
        console.error('[ADOPT] No auth token found in localStorage!');
        toast.error('Session expired. Please refresh the page and try again.');
        // Trigger a session refresh
        window.location.reload();
        return;
      }
      
      // Adopt each selected mustang
      const adoptionPromises = selectedMustangs.map(async (mustang) => {
        console.log(`[ADOPT] Adopting ${mustang.name} with ID: ${mustang.id}`);
        try {
          const response = await api.post('/user-mustangs/adopt-virtual', {
            virtual_mustang_id: mustang.id
          });
          console.log(`✅ ${mustang.name} adopted successfully`, response.data);
          return response;
        } catch (adoptError) {
          console.error(`[ADOPT] Error for ${mustang.name}:`, {
            status: adoptError.response?.status,
            statusText: adoptError.response?.statusText,
            data: adoptError.response?.data,
            message: adoptError.message,
            code: adoptError.code
          });
          throw adoptError;
        }
      });

      await Promise.all(adoptionPromises);

      const totalCoinsSpent = selectedMustangs.length * 125;
      toast.success(`🎉 Successfully adopted ${selectedMustangs.length} mustang${selectedMustangs.length > 1 ? 's' : ''}! ${totalCoinsSpent} coins spent. Redirecting to your Herd...`, {
        duration: 3000
      });
      
      // Clear selection
      setSelectedMustangs([]);
      
      // Refresh adoption status
      await fetchAdoptionStatus();
      
      // Redirect directly to the herd
      setTimeout(() => {
        window.location.href = '/the-herd';
      }, 1500);
    } catch (error) {
      console.error('❌ Error adopting mustangs:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Session expired. Please refresh the page and log in again.', {
          duration: 5000,
          action: {
            label: 'Refresh',
            onClick: () => window.location.reload()
          }
        });
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Subscription required. Please subscribe to adopt mustangs.');
      } else if (error.code === 'ERR_NETWORK') {
        // Check if this might be auth related
        const token = localStorage.getItem('session_token');
        if (!token || error.isAuthRelated) {
          toast.error('Session issue detected. Please refresh the page and log in again.', {
            duration: 5000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload()
            }
          });
        } else {
          toast.error('Network error. Please check your connection and try again.');
        }
      } else {
        toast.error(error.response?.data?.detail || `Failed to adopt mustangs: ${error.message}`);
      }
      
      // Refresh adoption status even on error to show updated limits
      await fetchAdoptionStatus();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
        <Navigation user={user} logout={logout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#D66238', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#372F2F' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // NOTE: Removed full-page subscription block - subscription is now checked when user clicks "Continue to Adoption Method"
  // This allows users to see the Virtual Corral page and understand what they're subscribing to

  // Main Virtual Corral Experience
  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Admin Preview Banner */}
        {isAdminPreview && !hasSubscription && (
          <div className="bg-yellow-100 border-2 border-yellow-600 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <Info className="w-6 h-6 text-yellow-800" />
              <div>
                <p className="font-bold text-yellow-900">Admin Preview Mode</p>
                <p className="text-sm text-yellow-800">You're viewing without a subscription for editing purposes. Regular users will see the paywall.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" style={{color: '#D66238'}}>Virtual Corral</h1>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600">Your virtual mustang adoption journey</p>
          </div>
          
          {/* My Ranch Access Button - Always visible if facility is qualified */}
          {facilityCompleted && (
            <Button
              onClick={() => window.location.href = '/the-herd'}
              className="px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              style={{backgroundColor: '#6FCFD3', color: 'white'}}
            >
              <HomeIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              The Herd ✓
            </Button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8 bg-white rounded-xl p-4 sm:p-6 shadow gap-4 sm:gap-2">
          <div className={`flex items-center gap-2 sm:gap-3 w-full sm:w-auto ${currentStep === 1 ? 'opacity-100' : 'opacity-50'}`}>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${facilityCompleted ? 'bg-green-600' : 'bg-gray-300'}`}>
              {facilityCompleted ? <Check className="w-4 h-4 sm:w-6 sm:h-6 text-white" /> : <span className="text-white text-sm sm:text-base font-bold">1</span>}
            </div>
            <span className="font-semibold text-sm sm:text-base">Design Facilities</span>
          </div>
          
          <ChevronRight className="w-6 h-6 text-gray-400 hidden sm:block" />
          
          <div className={`flex items-center gap-2 sm:gap-3 w-full sm:w-auto ${currentStep === 2 ? 'opacity-100' : 'opacity-50'}`}>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${adoptionMethod ? 'bg-green-600' : 'bg-gray-300'}`}>
              {adoptionMethod ? <Check className="w-4 h-4 sm:w-6 sm:h-6 text-white" /> : <span className="text-white text-sm sm:text-base font-bold">2</span>}
            </div>
            <span className="font-semibold text-sm sm:text-base">Choose Adoption Method</span>
          </div>
          
          <ChevronRight className="w-6 h-6 text-gray-400 hidden sm:block" />
          
          <div className={`flex items-center gap-2 sm:gap-3 w-full sm:w-auto ${currentStep === 3 ? 'opacity-100' : 'opacity-50'}`}>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMustangs.length > 0 ? 'bg-green-600' : 'bg-gray-300'}`}>
              {selectedMustangs.length > 0 ? <Check className="w-4 h-4 sm:w-6 sm:h-6 text-white" /> : <span className="text-white text-sm sm:text-base font-bold">3</span>}
            </div>
            <span className="font-semibold text-sm sm:text-base">Select Mustangs</span>
          </div>
        </div>

        {/* Step 1: Facilities */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{color: '#D66238'}}>Step 1: Design Your Facilities</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                Before adopting mustangs, you need BLM-compliant facilities. Use the integrated Facility Builder to design your layout.
              </p>

              {facilityCompleted ? (
                <div className="bg-green-50 border-2 border-green-600 rounded-xl p-4 sm:p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Check className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 flex-shrink-0" />
                    <h3 className="text-lg sm:text-xl font-bold text-green-800">Facilities Approved!</h3>
                  </div>
                  <p className="text-sm sm:text-base text-green-700 mb-4">Your facility meets all BLM requirements. You're ready to adopt mustangs!</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => window.location.href = '/facility'}
                      variant="outline"
                      style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                    >
                      <HomeIcon className="w-5 h-5 mr-2" />
                      View/Edit Facility
                    </Button>
                    <Button
                      onClick={() => {
                        // Check subscription before proceeding to adoption method
                        console.log('[VIRTUAL CORRAL] Continue button clicked');
                        console.log('[VIRTUAL CORRAL] hasSubscription:', hasSubscription);
                        console.log('[VIRTUAL CORRAL] isAdminPreview:', isAdminPreview);
                        console.log('[VIRTUAL CORRAL] loading:', loading);
                        
                        if (loading) {
                          console.log('[VIRTUAL CORRAL] Still loading, please wait...');
                          return;
                        }
                        
                        if (hasSubscription || isAdminPreview) {
                          console.log('[VIRTUAL CORRAL] Access granted, proceeding to step 2');
                          setCurrentStep(2);
                        } else {
                          console.log('[VIRTUAL CORRAL] No subscription, showing payment modal');
                          // Show subscription modal
                          setShowPaymentModal(true);
                        }
                      }}
                      style={{backgroundColor: '#D66238', color: 'white'}}
                      disabled={loading}
                    >
                      {loading ? 'Checking...' : 'Continue to Adoption Method'}
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border-2 border-yellow-600 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-yellow-800 mb-2">Facility Not Complete</h3>
                  <p className="text-yellow-700 mb-4">Design your BLM-compliant facilities to start your mustang adoption journey.</p>
                  <Button
                    onClick={() => window.location.href = '/facility'}
                    style={{backgroundColor: '#6FCFD3', color: 'white'}}
                  >
                    <HomeIcon className="w-5 h-5 mr-2" />
                    Open Facility Builder
                  </Button>
                </div>
              )}
            </div>

            {/* Quick Access Panel */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
              <h3 className="text-lg font-bold mb-3" style={{color: '#D66238'}}>Virtual Corral Features</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <HomeIcon className="w-6 h-6" style={{color: '#6FCFD3'}} />
                    <h4 className="font-bold">Facility Builder</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Design your BLM-compliant ranch facilities</p>
                  <Button
                    onClick={() => window.location.href = '/facility'}
                    size="sm"
                    style={{backgroundColor: '#6FCFD3', color: 'white'}}
                  >
                    Open Builder
                  </Button>
                </div>
                <div className="bg-white rounded-lg p-4 shadow opacity-50">
                  <div className="flex items-center gap-3 mb-2">
                    <HomeIcon className="w-6 h-6" style={{color: '#D66238'}} />
                    <h4 className="font-bold">The Herd</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Manage your virtual mustang herd</p>
                  <Button
                    onClick={() => toast.info('Complete mustang adoption to unlock Ranch management')}
                    size="sm"
                    variant="outline"
                    disabled={selectedMustangs.length === 0}
                  >
                    {selectedMustangs.length > 0 ? 'View Ranch' : 'Locked'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Adoption Method */}
        {currentStep === 2 && facilityCompleted && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#D66238'}}>Step 2: Choose Your Adoption Method</h2>
            <p className="text-gray-600 mb-4">
              Select how you want to adopt your virtual mustangs. Each method offers a unique experience.
            </p>
            
            {/* Rotation Info Banner */}
            {rotationInfo && (
              <div className="bg-gradient-to-r from-[#6FCFD3]/10 to-[#D66238]/10 border border-[#6FCFD3] rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-[#D66238]" />
                  <span className="font-semibold text-[#D66238]">Mustang Rotation Schedule</span>
                </div>
                <p className="text-sm text-gray-700">
                  Available mustangs rotate between adoption methods every <strong>11 days</strong>. 
                  Next rotation in <strong className="text-[#D66238]">{rotationInfo.days_until_next_rotation} day{rotationInfo.days_until_next_rotation !== 1 ? 's' : ''}</strong>.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Each mustang appears in only one method at a time – check back to find new arrivals!
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  id: 'olc',
                  title: 'Online Corral (OLC)',
                  description: 'Browse mustangs online and adopt from anywhere',
                  icon: '💻'
                },
                {
                  id: 'satellite',
                  title: 'Satellite Event',
                  description: 'Visit regional adoption events and meet mustangs in person',
                  icon: '📍'
                },
                {
                  id: 'holding',
                  title: 'Holding Facility',
                  description: 'Travel to BLM facilities to select and adopt',
                  icon: '🏛️'
                }
              ].map((method) => (
                <button
                  key={method.id}
                  onClick={() => {
                    setAdoptionMethod(method.id);
                    setCurrentStep(3);
                    fetchVirtualMustangs(method.id);
                  }}
                  className="p-6 border-2 rounded-xl hover:shadow-lg transition text-left"
                  style={{
                    borderColor: adoptionMethod === method.id ? '#D66238' : '#e5e7eb',
                    backgroundColor: adoptionMethod === method.id ? '#fff5f5' : 'white'
                  }}
                >
                  <div className="text-4xl mb-4">{method.icon}</div>
                  <h3 className="text-xl font-bold mb-2" style={{color: '#D66238'}}>{method.title}</h3>
                  <p className="text-gray-600">{method.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Mustang Selection */}
        {currentStep === 3 && facilityCompleted && adoptionMethod && (
          <div>
            {/* Back to Method Selection */}
            <Button
              onClick={() => {
                setCurrentStep(2);
                setAdoptionMethod(null);
                setVirtualMustangs([]);
                setSelectedMustangs([]);
              }}
              variant="outline"
              className="mb-4"
              style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
            >
              <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
              Change Adoption Method
            </Button>

            {/* Adoption Status Banner */}
            {adoptionStatus && (
              <div 
                className={`mb-6 p-4 rounded-lg shadow-lg ${
                  adoptionStatus.can_adopt 
                    ? adoptionStatus.remaining_adoptions <= 1 
                      ? 'bg-yellow-50 border-2 border-yellow-400' 
                      : 'bg-green-50 border-2 border-green-400'
                    : 'bg-red-50 border-2 border-red-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-bold ${
                      adoptionStatus.can_adopt 
                        ? adoptionStatus.remaining_adoptions <= 1 
                          ? 'text-yellow-700' 
                          : 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {adoptionStatus.can_adopt 
                        ? `You can adopt ${adoptionStatus.remaining_adoptions} more mustang${adoptionStatus.remaining_adoptions !== 1 ? 's' : ''}`
                        : '⚠️ Adoption Limit Reached'}
                    </p>
                    <p className={`text-sm ${
                      adoptionStatus.can_adopt 
                        ? adoptionStatus.remaining_adoptions <= 1 
                          ? 'text-yellow-600' 
                          : 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {adoptionStatus.can_adopt 
                        ? `${adoptionStatus.adoptions_in_current_window}/${adoptionStatus.max_adoptions} adoptions used in the last ${adoptionStatus.adoption_window_days} days`
                        : `You've adopted ${adoptionStatus.max_adoptions} mustangs in the last ${adoptionStatus.adoption_window_days} days. You can adopt again in ${adoptionStatus.days_until_next_adoption} day${adoptionStatus.days_until_next_adoption !== 1 ? 's' : ''}.`
                      }
                    </p>
                  </div>
                  {adoptionStatus.next_adoption_date && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Next adoption available</p>
                      <p className="font-semibold text-gray-700">{new Date(adoptionStatus.next_adoption_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Step 3: Select Your Mustangs</h2>
              <p className="text-gray-600 mb-4">
                Choose up to {virtualCorralSettings?.max_mustangs_per_event || 4} mustangs from this {adoptionMethod === 'olc' ? 'online corral' : adoptionMethod === 'satellite' ? 'satellite event' : 'holding facility'}.
                <span className="font-semibold ml-2">Cost: 125 coins per mustang</span>
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <span className="font-semibold">Selected: {selectedMustangs.length}/{virtualCorralSettings?.max_mustangs_per_event || 4}</span>
                {selectedMustangs.length > 0 && (
                  <span className="text-sm px-3 py-1 rounded-full" style={{backgroundColor: '#FEF3C7', color: '#92400E'}}>
                    💰 Total: {selectedMustangs.length * 125} coins
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  Your balance: {user?.ranch_coins || 0} coins
                </span>
                {selectedMustangs.length > 0 && (
                  <>
                    <Button
                      onClick={confirmAdoptions}
                      style={{backgroundColor: '#D66238', color: 'white'}}
                    >
                      Confirm Adoptions
                    </Button>
                    <Button
                      onClick={() => window.location.href = '/the-herd'}
                      variant="outline"
                      style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                    >
                      <HomeIcon className="w-5 h-5 mr-2" />
                      View The Herd
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Tabs for Browse / My Favorites */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="browse" className="flex items-center gap-2">
                  🐴 Browse ({virtualMustangs.length})
                </TabsTrigger>
                <TabsTrigger value="favorites" className="flex items-center gap-2">
                  <Heart className="w-4 h-4" /> My Favorites ({favoriteIds.length})
                </TabsTrigger>
              </TabsList>

              {/* Compare bar - shows when mustangs are selected for comparison */}
              {compareList.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex flex-wrap items-center gap-3">
                  <GitCompare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Compare ({compareList.length}/3):</span>
                  <div className="flex gap-2 flex-wrap">
                    {compareList.map(m => (
                      <span key={m.id} className="bg-white px-2 py-1 rounded text-sm flex items-center gap-1">
                        {m.name}
                        <button onClick={() => toggleCompare(m)} className="text-gray-400 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowCompareModal(true)}
                    disabled={compareList.length < 2}
                    style={{backgroundColor: '#6FCFD3', color: 'white'}}
                  >
                    Compare Now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCompareList([])}
                  >
                    Clear
                  </Button>
                </div>
              )}

              <TabsContent value="browse">
                {/* Selected Mustangs */}
                {selectedMustangs.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-6 mb-6">
                    <h3 className="font-bold mb-4" style={{color: '#D66238'}}>Your Selected Mustangs:</h3>
                    <div className="flex flex-wrap gap-4">
                      {selectedMustangs.map((mustang) => (
                        <div key={mustang.id} className="bg-white rounded-lg p-3 flex items-center gap-3 shadow">
                          <img src={resolveThumbUrl(mustang.image_url || mustang.photo_url)} alt={mustang.name} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
                          <div>
                            <p className="font-semibold">{mustang.name}</p>
                            <p className="text-xs text-gray-600">{mustang.color} {mustang.gender}</p>
                          </div>
                          <button onClick={() => removeMustang(mustang.id)} className="text-red-600 hover:text-red-800">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Mustangs */}
                {loadingMustangs ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto" 
                         style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
                    <p className="mt-4 text-gray-600">Loading available mustangs...</p>
                  </div>
                ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {virtualMustangs.map((mustang) => {
                  const isSelected = selectedMustangs.find(m => m.id === mustang.id);
                  const isFavorite = favoriteIds.includes(mustang.id);
                  const isInCompare = compareList.find(m => m.id === mustang.id);
                  return (
                    <div
                      key={mustang.id}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition"
                      style={{border: isSelected ? '3px solid #10b981' : '2px solid #6FCFD3'}}
                    >
                      {/* Mustang Photo - Vertical Rectangle */}
                      <div 
                        className="relative aspect-[3/4] overflow-hidden cursor-pointer hover:opacity-90 transition"
                        style={{backgroundColor: '#6FCFD3'}}
                        onClick={() => !isSelected && addMustangToSelection(mustang)}
                      >
                        {mustang.photo_url ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${mustang.photo_url}`}
                            alt={mustang.name} 
                            loading="lazy"
                            className="w-full h-full object-cover" 
                            style={{objectPosition: mustang.image_position || 'center center'}}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <span className="text-5xl mb-2">🐴</span>
                            <span className="text-white text-sm font-medium">No Photo Yet</span>
                          </div>
                        )}
                        {/* Favorite heart button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(mustang.id, mustang.name);
                          }}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition shadow-md"
                        >
                          <Heart 
                            className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                          />
                        </button>
                        {/* Compare checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompare(mustang);
                          }}
                          className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition shadow-md ${
                            isInCompare ? 'bg-blue-500 text-white' : 'bg-white/90 hover:bg-white text-gray-400'
                          }`}
                        >
                          <GitCompare className="w-4 h-4" />
                        </button>
                        {/* Photo count badge */}
                        {mustang.photo_urls && mustang.photo_urls.length > 1 && (
                          <div className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-bold bg-black/50 text-white">
                            📷 {mustang.photo_urls.length}
                          </div>
                        )}
                        {/* Selected badge */}
                        {isSelected && (
                          <div className="absolute bottom-2 right-2 px-2 py-1 rounded text-xs font-bold bg-green-500 text-white">
                            ✓ Selected
                          </div>
                        )}
                      </div>

                      {/* Mustang Info Card - Compact */}
                      <div className="p-3">
                        <h3 className="text-base font-bold mb-2 truncate" style={{color: '#D66238'}}>
                          {mustang.name}
                        </h3>
                        
                        <div className="text-xs text-gray-600 mb-3 space-y-1">
                          <div className="flex justify-between">
                            <span>{mustang.age || '?'}yo {mustang.gender || '?'}</span>
                            <span>{mustang.color || '?'}</span>
                          </div>
                          <div className="truncate">{mustang.hma || 'Unknown HMA'}</div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => !isSelected && addMustangToSelection(mustang)}
                            disabled={isSelected}
                            size="sm"
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            style={{borderColor: isSelected ? '#10b981' : '#6FCFD3', color: isSelected ? '#10b981' : '#6FCFD3'}}
                          >
                            {isSelected ? '✓ Added' : 'Adopt'}
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedMustangInfo(mustang);
                              setCurrentPhotoIndex(0);
                              setShowMustangInfo(true);
                            }}
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            style={{
                              backgroundColor: '#D66238',
                              color: '#fff'
                            }}
                          >
                            Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </TabsContent>

              {/* My Favorites Tab */}
              <TabsContent value="favorites">
                {favoriteMustangs.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No favorites yet</h3>
                    <p className="text-gray-500">Tap the heart icon on any mustang to add them to your favorites!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {favoriteMustangs.map((mustang) => {
                      const isSelected = selectedMustangs.find(m => m.id === mustang.id);
                      const isInCompare = compareList.find(m => m.id === mustang.id);
                      return (
                        <div
                          key={mustang.id}
                          className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition"
                          style={{border: isSelected ? '3px solid #10b981' : '2px solid #6FCFD3'}}
                        >
                          {/* Mustang Photo */}
                          <div 
                            className="relative aspect-[3/4] overflow-hidden cursor-pointer hover:opacity-90 transition"
                            style={{backgroundColor: '#6FCFD3'}}
                          >
                            {mustang.photo_url ? (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${mustang.photo_url}`}
                                alt={mustang.name} 
                                loading="lazy"
                                className="w-full h-full object-cover" 
                                style={{objectPosition: mustang.image_position || 'center center'}}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <span className="text-5xl mb-2">🐴</span>
                              </div>
                            )}
                            {/* Remove from favorites */}
                            <button
                              onClick={() => toggleFavorite(mustang.id, mustang.name)}
                              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition shadow-md"
                            >
                              <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                            </button>
                            {/* Compare checkbox */}
                            <button
                              onClick={() => toggleCompare(mustang)}
                              className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition shadow-md ${
                                isInCompare ? 'bg-blue-500 text-white' : 'bg-white/90 hover:bg-white text-gray-400'
                              }`}
                            >
                              <GitCompare className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Info */}
                          <div className="p-3">
                            <h3 className="text-base font-bold mb-2 truncate" style={{color: '#D66238'}}>
                              {mustang.name}
                            </h3>
                            <div className="text-xs text-gray-600 mb-3 space-y-1">
                              <div className="flex justify-between">
                                <span>{mustang.age || '?'}yo {mustang.gender || '?'}</span>
                                <span>{mustang.color || '?'}</span>
                              </div>
                              <div className="truncate">{mustang.hma || 'Unknown HMA'}</div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => addMustangToSelection(mustang)}
                                disabled={isSelected}
                                size="sm"
                                variant="outline"
                                className="flex-1 h-8 text-xs"
                                style={{borderColor: isSelected ? '#10b981' : '#6FCFD3', color: isSelected ? '#10b981' : '#6FCFD3'}}
                              >
                                {isSelected ? '✓ Added' : 'Adopt'}
                              </Button>
                              <Button
                                onClick={() => {
                                  setSelectedMustangInfo(mustang);
                                  setCurrentPhotoIndex(0);
                                  setShowMustangInfo(true);
                                }}
                                size="sm"
                                className="flex-1 h-8 text-xs"
                                style={{backgroundColor: '#D66238', color: '#fff'}}
                              >
                                Details
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* Compare Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b shrink-0">
            <DialogTitle style={{color: '#D66238'}}>Compare Mustangs</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            <div className={`grid gap-4 ${compareList.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {compareList.map((mustang) => (
                <div key={mustang.id} className="bg-gray-50 rounded-lg p-4">
                  {/* Photo */}
                  <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-gray-200">
                    {mustang.photo_url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${mustang.photo_url}`}
                        alt={mustang.name}
                        className="w-full h-full object-cover"
                        style={{objectPosition: mustang.image_position || 'center center'}}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">🐴</span>
                      </div>
                    )}
                  </div>
                  {/* Name */}
                  <h3 className="text-lg font-bold text-center mb-3" style={{color: '#D66238'}}>{mustang.name}</h3>
                  {/* Stats */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between border-b pb-1">
                      <span className="text-gray-500">Age</span>
                      <span className="font-medium">{mustang.age || '?'} years</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span className="text-gray-500">Gender</span>
                      <span className="font-medium">{mustang.gender || '?'}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span className="text-gray-500">Color</span>
                      <span className="font-medium">{mustang.color || '?'}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span className="text-gray-500">Height</span>
                      <span className="font-medium">{mustang.height || '?'}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                      <span className="text-gray-500">HMA</span>
                      <span className="font-medium truncate ml-2">{mustang.hma || '?'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Disposition</span>
                      <span className="font-medium">{mustang.disposition || '?'}</span>
                    </div>
                  </div>
                  {/* Adopt button */}
                  <Button
                    onClick={() => {
                      addMustangToSelection(mustang);
                      setShowCompareModal(false);
                    }}
                    className="w-full mt-4"
                    style={{backgroundColor: '#D66238', color: 'white'}}
                  >
                    Adopt {mustang.name}
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div className="shrink-0 bg-white border-t p-4">
            <Button onClick={() => setShowCompareModal(false)} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mustang Info Dialog */}
      <Dialog open={showMustangInfo} onOpenChange={setShowMustangInfo} modal={false}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[80vh] sm:max-h-[85vh] overflow-hidden flex flex-col p-0" style={{maxHeight: 'calc(100vh - 120px)'}}>
          <DialogHeader className="p-4 pb-2 border-b shrink-0">
            <DialogTitle style={{color: '#D66238'}}>{selectedMustangInfo?.name}</DialogTitle>
          </DialogHeader>
          {selectedMustangInfo && (
            <>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 min-h-0 overscroll-contain" style={{WebkitOverflowScrolling: 'touch'}}>
              {/* Photo Gallery */}
              <div className="relative w-full max-w-xs mx-auto rounded-lg overflow-hidden mb-4" style={{paddingBottom: '100%'}}>
                {(() => {
                  // Get all photos - use photo_urls array if available, otherwise fall back to single image
                  const photos = selectedMustangInfo.photo_urls?.length > 0 
                    ? selectedMustangInfo.photo_urls 
                    : [selectedMustangInfo.image_url || selectedMustangInfo.photo_url].filter(Boolean);
                  
                  if (photos.length === 0) {
                    return (
                      <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center" style={{backgroundColor: '#6FCFD3'}}>
                        <span className="text-8xl mb-2">🐴</span>
                        <span className="text-white font-medium">Photo Coming Soon</span>
                      </div>
                    );
                  }
                  
                  const currentPhoto = photos[currentPhotoIndex] || photos[0];
                  
                  return (
                    <>
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${currentPhoto}`}
                        alt={`${selectedMustangInfo.name} - Photo ${currentPhotoIndex + 1}`} 
                        className="absolute top-0 left-0 w-full h-full object-contain" 
                      />
                      
                      {/* Navigation arrows - only show if multiple photos */}
                      {photos.length > 1 && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                          >
                            ‹
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                          >
                            ›
                          </button>
                          
                          {/* Photo indicator dots */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {photos.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentPhotoIndex(idx);
                                }}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                  idx === currentPhotoIndex ? 'bg-white' : 'bg-white/50 hover:bg-white/75'
                                }`}
                              />
                            ))}
                          </div>
                          
                          {/* Photo counter */}
                          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                            {currentPhotoIndex + 1} / {photos.length}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              
              {/* Thumbnail strip for multiple photos */}
              {selectedMustangInfo.photo_urls?.length > 1 && (
                <div className="flex gap-2 justify-center mb-4 overflow-x-auto pb-2">
                  {selectedMustangInfo.photo_urls.map((photoUrl, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentPhotoIndex ? 'border-orange-500 scale-110' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={resolveThumbUrl(photoUrl)}
                        alt={`${selectedMustangInfo.name} thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Freezemark - displayed prominently if available */}
              {selectedMustangInfo.freezemark && (
                <div className="mb-4 p-3 rounded-lg" style={{backgroundColor: '#f0f9f9', border: '1px solid #6FCFD3'}}>
                  <span className="font-semibold text-sm" style={{color: '#D66238'}}>Freezemark ID:</span>
                  <span className="ml-2 font-mono text-lg font-bold" style={{color: '#372F2F'}}>{selectedMustangInfo.freezemark}</span>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3 text-sm mb-6">
                <div>
                  <p className="text-sm text-gray-600">Age</p>
                  <p className="font-semibold">{selectedMustangInfo.age} years</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="font-semibold">{selectedMustangInfo.gender}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">HMA Origin</p>
                  <p className="font-semibold">{selectedMustangInfo.hma}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Color</p>
                  <p className="font-semibold">{selectedMustangInfo.color}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Height</p>
                  <p className="font-semibold">{selectedMustangInfo.height}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Disposition</p>
                  <p className="font-semibold">{selectedMustangInfo.disposition}</p>
                </div>
              </div>
            </div>
            {/* Fixed buttons at bottom - always visible */}
            <div className="shrink-0 bg-white border-t p-4 flex flex-col sm:flex-row gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <Button 
                onClick={() => setShowMustangInfo(false)} 
                variant="outline"
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Close
              </Button>
              <Button
                onClick={handleAdoptMustang}
                style={{backgroundColor: '#D66238', color: 'white'}}
                className="flex-1 order-1 sm:order-2 py-3 text-base font-semibold"
                data-testid="adopt-mustang-btn"
              >
                🐴 Adopt {selectedMustangInfo?.name}
              </Button>
            </div>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Herd Membership Payment Modal */}
      <Dialog open={showHerdPaymentModal} onOpenChange={setShowHerdPaymentModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              🐴 Herd Membership Required
            </DialogTitle>
            <DialogDescription>
              To adopt and manage horses in The Herd, you need an active membership.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-4 border-2" style={{borderColor: '#D66238'}}>
              <div className="text-center mb-3">
                <p className="text-2xl font-bold" style={{color: '#D66238'}}>$4.95</p>
                <p className="text-xs text-gray-600">per month</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#6FCFD3'}} />
                  <p className="text-xs">Adopt up to 4 mustangs every 21 days</p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#6FCFD3'}} />
                  <p className="text-xs">Full access to The Herd features</p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#6FCFD3'}} />
                  <p className="text-xs">Track training and progress</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center">
              You have selected {selectedMustangs.length} mustang{selectedMustangs.length > 1 ? 's' : ''} to adopt
            </p>

            {/* Coupon Code Input */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{color: '#D66238'}}>
                Have a coupon code?
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                style={{borderColor: '#6FCFD3'}}
                disabled={processingPayment}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => setShowHerdPaymentModal(false)}
              variant="outline"
              className="flex-1"
              disabled={processingPayment}
            >
              Cancel
            </Button>
            <Button
              onClick={handleHerdMembershipPayment}
              className="flex-1"
              style={{
                backgroundColor: '#D66238', 
                color: 'white'
              }}
              disabled={processingPayment}
            >
              {processingPayment ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  💳 SUBSCRIBE NOW - $4.95/month
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Virtual Corral Subscription Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>Subscribe to Virtual Corral</DialogTitle>
            <DialogDescription>
              Access premium features with a monthly subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{color: '#D66238'}}>
                ${virtualCorralSettings?.monthly_price || 19.95}
              </p>
              <p className="text-xs text-gray-600">per month</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-3">
              <h4 className="font-semibold mb-2 text-sm" style={{color: '#D66238'}}>What&apos;s Included:</h4>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span>Choose adoption method (OLC, Satellite, Holding)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span>Adopt up to 4 virtual mustangs per event</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span>Access to The Herd management</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span>Track mustang care and training</span>
                </li>
              </ul>
            </div>
            
            {/* Coupon Code Input */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{color: '#D66238'}}>
                Have a coupon code?
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                style={{borderColor: '#6FCFD3'}}
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              onClick={() => setShowPaymentModal(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                setProcessingPayment(true);
                try {
                  const originUrl = window.location.protocol === 'https:' 
                    ? window.location.origin 
                    : `https://${window.location.host}`;
                  
                  const response = await api.post('/subscriptions/create-checkout', {
                    plan_type: 'monthly_unlimited', 
                    origin_url: originUrl,
                    coupon_code: couponCode?.trim() || undefined
                  });
                  
                  window.location.href = response.data.url;
                } catch (error) {
                  console.error('Payment error:', error);
                  if (error.response?.status === 404) {
                    toast.error('Invalid coupon code');
                  } else if (error.response?.status === 400) {
                    toast.error(error.response.data.detail || 'Coupon is expired or limit reached');
                  } else {
                    toast.error('Payment failed. Please try again.');
                  }
                  setProcessingPayment(false);
                }
              }}
              className="flex-1"
              style={{backgroundColor: '#D66238', color: 'white'}}
              disabled={processingPayment}
            >
              {processingPayment ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscribe Now
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VirtualCorral;
